import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,  Result, SectionTitle, Image, Separator,CorrectionBox,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider} from '../../../../components/course/CourseComponents';
import screen4 from '../screen4.jpg';

const Chapitre4Content = () => {
  return (
    <>
    <InfoBox title="Objectif du chapitre" color="indigo">
      <p className="mb-0">
        Afficher les X et O sur le plateau et indiquer le joueur courant.
      </p>
    </InfoBox>      

    <Separator type="text" text="Affichage des X et O" color="gray-darker" size="md" />
    <SectionTitle>🎨 1/ Remplir le plateau grâce au tableau</SectionTitle>

    <p>On va utiliser notre tableau en mémoire qui contient tous les coups joués, pour afficher notre plateau rempli.</p>
    <p>Pour cela, on va parcourir tout notre tableau avec une double boucle for :</p>
    <ul className="list-disc list-inside mb-4">
      <li>Si la case [i][j] vaut "X", alors on affiche un X dans la bonne case du plateau</li>
      <li>Si la case [i][j] vaut "O", alors on affiche un O</li>
      <li>Si la case est vide, on ne fait rien</li>
    </ul>

    <p>Pour afficher un X ou un O, on utilise les fonctions suivante de p5.js :</p>
    <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>textSize(taille)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Définit la taille du texte<br/>- Paramètre 1 : taille du texte</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>textAlign(align)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Définit l'alignement du texte<br/>- Paramètre 1 : alignement (LEFT, CENTER, RIGHT)</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>text(message, x, y)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Affiche du texte<br/>- Paramètre 1 : texte à afficher<br/>- Paramètre 2 : position en x<br/>- Paramètre 3 : position en y</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>textStyle(style)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Définit le style du texte<br/>- Paramètre 1 : style du texte (NORMAL, BOLD, ITALIC)</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>fill(rouge, vert, bleu)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Détermine la couleur du texte affiché</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>


    <p>Le X ou le O doit être affiché au centre de la case. Il faut donc déterminer le centre de la case et s'assurer que le texte sera bien centré avec <InlineCode>textAlign(CENTER, CENTER)</InlineCode>.</p>

    <Note type="tip" title="🤔 Comment on fait pour calculer le centre de la case ?">
    <p>Pour calculer le centre de la case en coordonnées (ligne, colonne), <InlineCode>ligne</InlineCode> represente le numero de ligne et <InlineCode>colonne</InlineCode> le numero de colonne :</p>
    <ul className="list-disc list-inside mb-4">
      <li>En X : on détermine le bord gauche en faisant <InlineCode>j * largeur_case</InlineCode>, puis on ajoute la moitié de la case : <InlineCode>largeur_case / 2</InlineCode></li>
      <li>En Y : on détermine le haut de la case en faisant  <InlineCode>  i * hauteur_case</InlineCode>, puis on ajoute la moitié de la case : <InlineCode>hauteur_case / 2</InlineCode></li>
    </ul>

    <Image src={screen4} alt="Grille du Morpion" width="370px" height="370px" borderRadius="xl" shadow={true} border={false} className="object-contain" />
    <br/>
    <p>Ce qui donne : </p>

    <CodeBlock language="javascript">
{`let centre_case_x = colonne * largeur_case + largeur_case / 2;
let centre_case_y = ligne * hauteur_case + hauteur_case / 2;`}
    </CodeBlock>

    </Note>

   

    <Note type="exercice" title="🧪 À toi de jouer : affiche les croix et les cercles">
      En bas de la fonction <InlineCode>draw()</InlineCode> :<br/>
      &nbsp;&nbsp;- Assure-toi que le texte est centré avec <InlineCode>textAlign(CENTER, CENTER)</InlineCode><br/>
      &nbsp;&nbsp;- Choisis une taille de texte suffisamment grande avec <InlineCode>textSize(150)</InlineCode><br/>
      &nbsp;&nbsp;- Utilise le style <InlineCode>textStyle(NORMAL)</InlineCode><br/>
      &nbsp;&nbsp;- Crée une double boucle for pour parcourir les lignes et colonnes<br/>
      &nbsp;&nbsp;- Pour chaque case, détermine son centre en X et Y<br/>
      &nbsp;&nbsp;- Si la case contient "X" : affiche un X rouge<br/>
      &nbsp;&nbsp;- Si elle contient "O" : affiche un O bleu<br/>
      <br/>
      <Result>Quand tu cliques sur le plateau, un X rouge et un O bleu apparaissent en alternance.</Result>

      <CorrectionBox>
        <CodeBlock language="javascript">
{`// Afficher les X et O avec couleurs
textAlign(CENTER, CENTER);
textSize(150);
textStyle(NORMAL);
for (let ligne = 0; ligne < 3; ligne++) {
  for (let colonne = 0; colonne < 3; colonne++) {
    let centre_case_x = colonne * largeur_case + largeur_case / 2;
    let centre_case_y = ligne * hauteur_case + hauteur_case / 2;
    if (grille[ligne][colonne] == "X") {
      fill(255, 0, 0); // Rouge
      text("X", centre_case_x, centre_case_y);
    } else if (grille[ligne][colonne] == "O") {
      fill(0, 0, 255); // Bleu
      text("O", centre_case_x, centre_case_y);
    }
  }
}`}
        </CodeBlock>
      </CorrectionBox>
    </Note>

    <Separator type="text" text="Affichage du joueur courant" color="gray-darker" size="md" />
    <SectionTitle>👤 2/ Affichage du joueur courant</SectionTitle>

    <p>Maintenant on va indiquer quel joueur doit jouer, en affichant son nom en haut à gauche.</p>
    <p>Pour cela :</p>
    <ul className="list-disc list-inside mb-4">
      <li>Choisis une taille de texte plus petite</li>
      <li>Utilise un style en gras</li>
      <li>Aligne le texte à gauche avec <InlineCode>textAlign(LEFT)</InlineCode></li>
    </ul>

    <CodeBlock language="javascript">
{`textAlign(LEFT);
textSize(16);
textStyle(BOLD);`}
    </CodeBlock>

    <p>Ensuite, si <InlineCode>joueur_courant == "X"</InlineCode> : affiche "Joueur X" en rouge<br/>
    Sinon : affiche "Joueur O" en bleu</p>

    <Note type="exercice" title="🧪 À toi de jouer : affiche le joueur courant">
      En bas de la fonction <InlineCode>draw()</InlineCode> :<br/>
      &nbsp;&nbsp;- Aligne le texte à gauche<br/>
      &nbsp;&nbsp;- Mets une taille de texte de 16<br/>
      &nbsp;&nbsp;- Utilise le style BOLD<br/>
      &nbsp;&nbsp;- Ajoute une condition selon que joueur_courant vaut "X" ou "O"<br/>
      &nbsp;&nbsp;- Affiche le texte "Joueur X" ou "Joueur O" avec la bonne couleur à la position (2, 10)<br/>
      <br/>
      <Result>Le joueur courant est affiché en haut à gauche, avec la bonne couleur.</Result>

      <CorrectionBox>
        <CodeBlock language="javascript">
{`// Afficher le joueur courant avec couleur
textAlign(LEFT);
textSize(16);
textStyle(BOLD);
if (joueur_courant == "X") {
  fill(255, 0, 0); // Rouge
  text("Joueur X", 2, 10);
} else {
  fill(0, 0, 255); // Bleu
  text("Joueur O", 2, 10);
}`}
        </CodeBlock>
      </CorrectionBox>
    </Note>

    <Separator type="text" text="Code complet" color="gray-darker" size="md" />
    <SectionTitle>✅ Code complet (Affichage des X et O et du joueur courant)</SectionTitle>

          <CodeBlock language="javascript">
{`// Jeu 2 : le Morpion

let largeur_case;
let hauteur_case;
let grille;
let joueur_courant = "X";

function setup() {
  createCanvas(600, 600);
  largeur_case = width / 3;
  hauteur_case = height / 3;
 
  grille = [
    ["", "", ""],
    ["", "", ""],
    ["", "", ""]
  ];
}

function draw() {
  background(220, 220, 220);

  // Plateau de jeu
  strokeWeight(4);
  for (let i = 1; i < 3; i++) {
    line(i * largeur_case, 0, i * largeur_case, height);
    line(0, i * hauteur_case, width, i * hauteur_case);
  }
 
  // Afficher les X et O avec couleurs
  textAlign(CENTER, CENTER);
  textSize(150);
  textStyle(NORMAL);
    for (let ligne = 0; ligne < 3; ligne++) {
    for (let colonne = 0; colonne < 3; colonne++) {
      let centre_case_x = colonne * largeur_case + largeur_case / 2;
      let centre_case_y = ligne * hauteur_case + hauteur_case / 2;
      if (grille[ligne][colonne] == "X") {
        fill(255, 0, 0); // Rouge
        text("X", centre_case_x, centre_case_y);
      } else if (grille[ligne][colonne] == "O") {
        fill(0, 0, 255); // Bleu
        text("O", centre_case_x, centre_case_y);
      }
    }
  }

  // Afficher le joueur courant avec couleur
  textAlign(LEFT);
  textSize(16);
  textStyle(BOLD);
  if (joueur_courant == "X") {
    fill(255, 0, 0); // Rouge
    text("Joueur X", 2, 10);
  } else {
    fill(0, 0, 255); // Bleu
    text("Joueur O", 2, 10);
  }
}

function mousePressed() {
  let case_x = floor(mouseY / hauteur_case);
  let case_y = floor(mouseX / largeur_case);

  if (case_x < 0 || case_x > 2 || case_y < 0 || case_y > 2)  
    return; // Clique en dehors du plateau
 
  if (grille[case_x][case_y] == "") {
    grille[case_x][case_y] = joueur_courant;
   
    // Changement de joueur
    if (joueur_courant == "X") joueur_courant = "O";
    else joueur_courant = "X";
  }
}`}
    </CodeBlock>

    <Note type="info" title="💡 Prêt pour la suite ?">
      Maintenant que tu as un morpion fonctionnel avec l'affichage des X et O, nous allons ajouter la détection du gagnant ou de l'égalité !
    </Note>
    </>
  );
};

export default Chapitre4Content; 